// @ts-nocheck
import { mapObj, compose, pick, isEmpty, nonEmpty } from './obj'
import {
  includes as getIncludes,
  productTable as getProductTableSchema,
} from '../redux/contentful-selectors'
import { Contentful } from '../types/contentful'
import { FsCore } from '../types/fs-core'

// TODO: Type the rest of this file properly

const sys = (s: any = {}) => s.sys || {}

const getFields = ({ fields = {} }: any) => fields

const contentfulEnv = process?.env?.CONTENTFUL_ENV || ''

export const getAuthorName = (model: any = {}) => getFields(model).name || null
export const getAuthorUrl = (model = {}) => getFields(model).url || null

export const articleSchema = (page: any = {}) => getFields(page).articleSchema || null

export const contentId = (entity: any = {}) => sys(entity).id
export const variantId = (entity = {}) => entity.variant
export const layoutId = (entity = {}) => entity.layout
export const spaceId = (entity: any = {}) => sys(entity).spaceId || sys(entity).space?.sys?.id
export const contentTypeId = (entity: any = {}) =>
  sys(entity)?.contentTypeId || sys(entity)?.contentType?.sys?.id

const sysId = contentId

export const pageTitle = (page: any = {}) => getFields(page).title || ''

export const createdAt = (entity: any = {}) => sys(entity)?.createdAt
export const updatedAt = (entity: any = {}) => sys(entity)?.updatedAt

export const dataProps = (entity = {}) =>
  nonEmpty({
    'data-content-type': contentTypeId(entity),
    'data-entity-id': contentId(entity),
    'data-space-id': spaceId(entity),
    'data-entity-name': entity.name || '',
    'data-entity-variant': variantId(entity) || '',
    'data-entity-layout': layoutId(entity) || '',
    'data-environment': contentfulEnv,
  })

export const findInclude = <T>(includes: Contentful.Includes<T>, id: string) =>
  Object.values(includes).find((it) => contentTypeId(it) === id)

export const findIncludes = <T>(includes: Contentful.Includes<T>, id: string) =>
  Object.values(includes).filter((it) => contentTypeId(it) === id)

/**
 * A contentful Entity
 * @typedef {Object} Entity
 * @property {Object} sys - The System info object of the entity
 * @property {string} sys.id - The id of entity
 */

/**
 * findEntities
 * Given a list of root entities, will return them from the contentful
 * includes state.
 *
 * @param {Object<string, Object>} includes - The contentful includes map
 * @param {Entity[]} entities - List of contentful entities to find
 * @
 */
export const findEntities = (includes, entities) => {
  const entityIds = entities.map(contentId)
  return Object.values(pick(entityIds, includes))
}

const file = (s = {}) => s.file || {}

export const fileType = (entity = {}) => file(entity).contentType || ''

// Modifiers
export const assignContentTypeId =
  (id) =>
  ({ sys, ...entity }) => ({
    ...entity,
    sys: { ...sys, contentTypeId: id },
  })

/* Simpify entities */
const simplifyEntitySys = (sys) =>
  nonEmpty({
    id: sys?.id,
    spaceId: sys?.space?.sys?.id,
    contentTypeId: sys?.contentType?.sys?.id,
    total: sys?.total,
    createdAt: sys?.createdAt,
    updatedAt: sys?.updatedAt,
    trimmedContent: sys?.trimmedContent,
  })

const simplifyEntrySys = (entry) => (entry?.sys?.id ? { sys: { id: entry?.sys?.id } } : undefined)

const simplifyEntityFields = (fields) =>
  nonEmpty(
    !Array.isArray(fields?.entries)
      ? fields
      : {
          ...fields,
          entries: fields.entries.map(simplifyEntrySys).filter((i) => i),
        },
  )

const simplifyEntity = (entity) => {
  if (!entity || isEmpty(entity)) return {}

  const { fields, sys } = entity
  return nonEmpty(
    {
      fields: simplifyEntityFields(fields),
      sys: simplifyEntitySys(sys),
    },
    {},
  )
}

const simplifyEntities = ({ includes = {}, rootEntity = {}, rootEntities = [], ...rest }) => ({
  ...rest,
  rootEntity: simplifyEntity(rootEntity),
  rootEntities: rootEntities.map(simplifyEntity),
  includes: mapObj(simplifyEntity, includes),
})

/* Simplify Product dataset */

export interface SimplifyProductDatasetsOptions {
  includes: Contentful.Includes<any>
  [key: string]: any
}
const simplifyProductDatasets = ({ includes = {}, ...rest }: SimplifyProductDatasetsOptions) => {
  const productTables = Object.entries(includes).filter(
    ([, include]) => contentTypeId(include) === 'productTable',
  )

  const productDatasets = Object.fromEntries(
    productTables.map(([tableId, table]) => {
      const dataset = includes[sysId(table.fields.dataset)]

      if (dataset?.fields?.query) return [tableId, table]

      const data = (dataset?.fields?.data || [])
        .filter(({ id }) => (!table.fields.filterIds ? true : table.fields.filterIds.includes(id)))
        .slice(0, table.fields.rowCount)

      return [
        tableId,
        {
          ...table,
          fields: {
            ...table.fields,
            filteredDataset: data,
          },
        },
      ]
    }),
  )

  return {
    ...rest,
    includes: { ...includes, ...productDatasets },
  } as SimplifyProductDatasetsOptions
}

export const simplifyCtx = compose(simplifyProductDatasets, simplifyEntities)

export const getProductTableRelations = (
  state: any,
  productTable: Contentful.ProductTableComponent,
) => {
  const includes: Contentful.Includes<any> = getIncludes(state)

  return {
    productTableSchema: getProductTableSchema(
      state,
      productTable.fields.schema.sys.id,
    ) as Contentful.ProductTableSchemaComponent,
    productDataset: includes[
      productTable.fields.dataset.sys.id
    ] as Contentful.TypedModule<Contentful.ProductDatasetFields>,
  }
}

export type TableRelationsReturn = {
  productTable?: Contentful.ProductTableComponent
  productTableSchema?: Contentful.ProductTableSchemaComponent
  productDataset?: Contentful.TypedModule<Contentful.ProductDatasetFields>
}
export const getProductTableWithRelations = (state: FsCore.State, productTableId: string) => {
  const includes = getIncludes(state)
  const productTable = includes[productTableId] as Contentful.ProductTableComponent

  if (!productTable) return {}
  const { productTableSchema, productDataset } = getProductTableRelations(state, productTable)
  return {
    productTable,
    productTableSchema,
    productDataset,
  } as TableRelationsReturn
}
